import React from "react"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { useToast } from "~/ui/use-toast"
import { PhoneVerificationStep } from "./components/PhoneVerificationStep"

const USER_PHONE_VERIFY_MUTATION = gql(/* GraphQL */ `
  mutation VerifyPhoneDialogUserPhoneVerify($input: UserPhoneVerifyInput!) {
    userPhoneVerify(input: $input) {
      userPhone {
        id
        phone
        phoneFormatted
        status
      }
    }
  }
`)

const USER_PHONE_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation VerifyPhoneDialogUserPhoneCreate($input: UserPhoneCreateInput!) {
    userPhoneCreate(input: $input) {
      userPhone {
        id
        phone
        phoneFormatted
        status
      }
    }
  }
`)

interface VerifyPhoneDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userPhoneId: string
  phone: string
  phoneFormatted: string
  onSuccess?: () => void
}

export const VerifyPhoneDialog: React.FC<VerifyPhoneDialogProps> = ({
  open,
  onOpenChange,
  userPhoneId,
  phone,
  phoneFormatted,
  onSuccess,
}) => {
  const [userPhoneVerify, userPhoneVerifyResult] = useSafeMutation(USER_PHONE_VERIFY_MUTATION)
  const [userPhoneCreate, userPhoneCreateResult] = useSafeMutation(USER_PHONE_CREATE_MUTATION)
  const { toast } = useToast()

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleVerify = async (code: string) => {
    const result = await userPhoneVerify({
      variables: {
        input: {
          userPhoneId,
          code,
        },
      },
    })

    if (result.errors) {
      return
    }

    toast({
      title: "Phone Verified",
      description: "Your phone number has been successfully verified.",
      variant: "default",
    })
    onSuccess?.()
    handleClose()
  }

  const handleResend = async () => {
    const result = await userPhoneCreate({
      variables: {
        input: {
          input: {
            phone,
          },
        },
      },
    })

    if (result.errors) {
      return
    }

    toast({
      title: "Verification Code Sent",
      description: "A new verification code has been sent to your phone.",
      variant: "default",
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Enter Verification Code</DialogTitle>
        </DialogHeader>
        <PhoneVerificationStep
          phone={phoneFormatted}
          onVerify={handleVerify}
          onSendAgain={handleResend}
          onCancel={handleClose}
          verificationError={userPhoneVerifyResult.error}
          isVerifying={userPhoneVerifyResult.loading}
          isSendingAgain={userPhoneCreateResult.loading}
          description={`A verification code was sent to ${phoneFormatted}. Please check your messages and input the code provided.`}
        />
        {userPhoneCreateResult.error && (
          <div className="px-6 pb-4">
            <GraphqlError error={userPhoneCreateResult.error} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
